import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation, Trans } from "react-i18next";
import {
  ArrowRight, ArrowLeft, ShieldCheck, Check, Sparkles, User, Target, BadgeCheck,
  FileUp, X, Plus, FileText, ExternalLink, Building2, RefreshCw, PartyPopper, Linkedin,
} from "lucide-react";
import api, { apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { roleHome, FIELDS, LANGUAGES, COUNTRIES } from "@/lib/constants";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner } from "@/components/ui-bits";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TOTAL_STEPS = 4;

function ChipToggle({ options, value, onChange, testid }) {
  const toggle = (o) => onChange(value.includes(o) ? value.filter((v) => v !== o) : [...value, o]);
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => {
        const on = value.includes(o);
        return (
          <button key={o} type="button" onClick={() => toggle(o)} data-testid={`${testid}-${o.replace(/\W+/g, "-").toLowerCase()}`}
            className={`text-sm font-medium rounded-full px-3 py-1.5 border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"}`}>
            {o}
          </button>
        );
      })}
    </div>
  );
}

function TagInput({ value, onChange, placeholder, testid }) {
  const [text, setText] = useState("");
  const add = () => { const v = text.trim(); if (v && !value.includes(v)) onChange([...value, v]); setText(""); };
  return (
    <div>
      <div className="flex gap-2">
        <Input data-testid={testid} value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder} className="h-11 rounded-lg" />
        <Button type="button" variant="outline" onClick={add} className="rounded-lg shrink-0 h-11"><Plus size={16} /></Button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2.5">
          {value.map((v) => (
            <span key={v} className="inline-flex items-center gap-1 bg-secondary text-secondary-foreground rounded-full pl-3 pr-1.5 py-1 text-sm">
              {v}
              <button type="button" onClick={() => onChange(value.filter((x) => x !== v))} className="text-muted-foreground hover:text-destructive"><X size={13} /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function FieldRow({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label} {hint && <span className="text-muted-foreground font-normal">{hint}</span>}</Label>
      {children}
    </div>
  );
}

export default function ExpertRegister() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const inviteToken = params.get("invite");
  const cvRef = useRef();

  const [invite, setInvite] = useState(null);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [id, setId] = useState({ name: "", email: "", password: "" });
  const [pro, setPro] = useState({ title: "", headline: "", bio: "", years_experience: 0, country: "Remote", city: "", languages: ["English"], linkedin: "" });
  const [exp, setExp] = useState({ fields: [], expertise: [], certifications: [], achievements: [] });
  const [cv, setCv] = useState({ url: null, name: "", parsing: false, imported: false });
  const [consent, setConsent] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [terms, setTerms] = useState(false);

  const [challenge, setChallenge] = useState(null);
  const [answer, setAnswer] = useState("");
  const [robot, setRobot] = useState(false);
  const [honeypot, setHoneypot] = useState("");

  const loadChallenge = () => api.get("/auth/human-challenge").then(({ data }) => setChallenge(data)).catch(() => {});
  useEffect(() => { loadChallenge(); }, []);

  useEffect(() => {
    if (!inviteToken) return;
    api.get(`/auth/coach-invite/${inviteToken}`).then(({ data }) => {
      if (data?.status === "pending") {
        setInvite(data);
        setId((p) => ({ ...p, name: p.name || data.name || "", email: p.email || data.email || "" }));
      }
    }).catch(() => {});
  }, [inviteToken]);

  const importCv = async (file) => {
    if (!file) return;
    setCv((c) => ({ ...c, parsing: true }));
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/auth/parse-cv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const p = data.parsed || {};
      setCv({ url: data.cv_url, name: data.cv_name, parsing: false, imported: true });
      if (data.extracted) {
        setPro((prev) => ({
          ...prev,
          title: p.title || prev.title,
          headline: p.headline || prev.headline,
          bio: p.bio || prev.bio,
          years_experience: p.years_experience || prev.years_experience,
          languages: (p.languages && p.languages.length) ? p.languages : prev.languages,
        }));
        setExp((prev) => ({
          ...prev,
          fields: (p.fields && p.fields.length) ? p.fields : prev.fields,
          expertise: (p.expertise && p.expertise.length) ? p.expertise : prev.expertise,
          certifications: (p.certifications && p.certifications.length) ? p.certifications : prev.certifications,
        }));
        toast.success(t("auth.wizard.cvToastImported"));
      } else {
        toast.message(t("auth.wizard.cvToastNoText"));
      }
    } catch (err) {
      setCv((c) => ({ ...c, parsing: false }));
      toast.error(apiError(err, t("auth.wizard.cvToastFailed")));
    }
  };

  const validate = (s) => {
    if (s === 1) {
      if (!id.name.trim()) return t("auth.wizard.errName");
      if (!EMAIL_RE.test(id.email)) return t("auth.wizard.errEmail");
      if (id.password.length < 6) return t("auth.wizard.errPassword");
    }
    if (s === 2 && !pro.title.trim()) return t("auth.wizard.errTitle");
    if (s === 3 && exp.fields.length === 0) return t("auth.wizard.errFields");
    return null;
  };

  const next = () => {
    const err = validate(step);
    if (err) { toast.error(err); return; }
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!consent) { toast.error(t("auth.wizard.errConsent")); return; }
    if (!terms) { toast.error(t("auth.wizard.errTerms")); return; }
    if (!robot || !answer.trim() || !challenge) { toast.error(t("auth.wizard.errRobot")); return; }
    setSubmitting(true);
    try {
      const location = pro.city ? `${pro.city}, ${pro.country}` : pro.country;
      const { data } = await api.post("/auth/register/expert", {
        name: id.name, email: id.email, password: id.password,
        title: pro.title, headline: pro.headline, bio: pro.bio,
        years_experience: Number(pro.years_experience) || 0,
        languages: pro.languages, location, linkedin: pro.linkedin,
        fields: exp.fields, expertise: exp.expertise,
        certifications: exp.certifications, achievements: exp.achievements,
        cv_url: cv.url, cv_name: cv.name,
        privacy_consent: consent, marketing_consent: marketing, terms_consent: terms,
        coach_invite_token: inviteToken || undefined,
        human_check: { token: challenge.token, answer, website: honeypot },
      });
      setUser(data.user);
      setSubmitted(true);
    } catch (err) {
      toast.error(apiError(err, t("auth.couldNotRegister")));
      loadChallenge(); setAnswer(""); setRobot(false);
    } finally { setSubmitting(false); }
  };

  const stepMeta = [
    { icon: User, key: "stepIdentity" },
    { icon: BadgeCheck, key: "stepProfile" },
    { icon: Target, key: "stepExpertise" },
    { icon: ShieldCheck, key: "stepReview" },
  ];

  if (submitted) {
    return (
      <WizardLayout>
        <div className="w-full max-w-md mx-auto text-center animate-fade-up" data-testid="wizard-submitted">
          <span className="mx-auto grid place-items-center w-16 h-16 rounded-full bg-success/10 text-success mb-6">
            <PartyPopper size={30} strokeWidth={1.6} />
          </span>
          <h1 className="text-2xl font-semibold tracking-tight">{t("auth.wizard.submittedTitle")}</h1>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{t("auth.wizard.submittedBody")}</p>
          <Button data-testid="wizard-go-workspace" onClick={() => navigate("/expert")} className="mt-7 h-11 rounded-lg gap-1.5 w-full">
            {t("auth.wizard.submittedCta")} <ArrowRight size={16} />
          </Button>
        </div>
      </WizardLayout>
    );
  }

  return (
    <WizardLayout>
      <div className="w-full max-w-lg mx-auto">
        {/* Stepper */}
        <div className="flex items-center gap-1.5 mb-8" data-testid="wizard-stepper">
          {stepMeta.map((m, i) => {
            const n = i + 1;
            const active = n === step; const done = n < step;
            return (
              <React.Fragment key={m.key}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`grid place-items-center w-8 h-8 rounded-full shrink-0 transition-colors ${done ? "bg-primary text-primary-foreground" : active ? "bg-primary/10 text-primary ring-2 ring-primary/30" : "bg-secondary text-muted-foreground"}`}>
                    {done ? <Check size={15} /> : <m.icon size={15} strokeWidth={1.9} />}
                  </span>
                  <span className={`hidden sm:block text-xs font-medium truncate ${active || done ? "text-foreground" : "text-muted-foreground"}`}>{t(`auth.wizard.${m.key}`)}</span>
                </div>
                {n < TOTAL_STEPS && <span className={`flex-1 h-px ${done ? "bg-primary" : "bg-border"}`} />}
              </React.Fragment>
            );
          })}
        </div>

        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-primary mb-2">{t("auth.wizard.step", { n: step, total: TOTAL_STEPS })}</p>
        <h1 className="text-2xl font-semibold tracking-tight">{t(`auth.wizard.s${step}Title`)}</h1>
        <p className="text-sm text-muted-foreground mt-1.5">{t(`auth.wizard.s${step}Sub`)}</p>

        {invite && step === 1 && (
          <div data-testid="coach-invite-banner" className="mt-6 rounded-lg border border-primary/30 bg-primary/5 p-4">
            <p className="text-sm font-medium text-foreground flex items-center gap-1.5">
              <Sparkles size={15} className="text-primary shrink-0" /> {t("auth.coachInviteTitle", { company: invite.company_name })}
            </p>
            <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{t("auth.coachInviteBody")}</p>
          </div>
        )}

        <div className="mt-7 space-y-4">
          {/* STEP 1 — Identity */}
          {step === 1 && (
            <div className="space-y-4 animate-fade-up" data-testid="wizard-step-1">
              <FieldRow label={t("auth.wizard.name")}>
                <Input data-testid="w-name" value={id.name} onChange={(e) => setId({ ...id, name: e.target.value })} placeholder="Alex Morgan" className="h-11 rounded-lg" />
              </FieldRow>
              <FieldRow label={t("auth.wizard.email")}>
                <Input data-testid="w-email" type="email" value={id.email} onChange={(e) => setId({ ...id, email: e.target.value })} placeholder="you@email.com" className="h-11 rounded-lg" />
              </FieldRow>
              <FieldRow label={t("auth.wizard.password")}>
                <Input data-testid="w-password" type="password" value={id.password} onChange={(e) => setId({ ...id, password: e.target.value })} placeholder={t("auth.wizard.passwordHint")} className="h-11 rounded-lg" />
              </FieldRow>
            </div>
          )}

          {/* STEP 2 — Profile + CV import */}
          {step === 2 && (
            <div className="space-y-5 animate-fade-up" data-testid="wizard-step-2">
              <div className="rounded-xl border border-primary/25 bg-primary/[0.04] p-4">
                <div className="flex items-center gap-2.5">
                  <span className="grid place-items-center w-9 h-9 rounded-lg bg-primary/10 text-primary shrink-0"><Sparkles size={17} strokeWidth={1.75} /></span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground">{t("auth.wizard.cvImportTitle")}</p>
                    <p className="text-xs text-muted-foreground">{t("auth.wizard.cvImportSub")}</p>
                  </div>
                </div>
                <input ref={cvRef} type="file" accept=".pdf,.doc,.docx,.txt,application/pdf" hidden data-testid="w-cv-input" onChange={(e) => importCv(e.target.files[0])} />
                {cv.url ? (
                  <div className="mt-3 flex items-center gap-3 rounded-lg border border-border bg-card p-3">
                    <span className="grid place-items-center w-9 h-9 rounded-lg bg-success/10 text-success shrink-0"><FileText size={17} /></span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{cv.name}</p>
                      <a href={fileUrl(cv.url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">{t("auth.wizard.cvView")} <ExternalLink size={11} /></a>
                    </div>
                    <Button type="button" variant="outline" size="sm" className="rounded-lg shrink-0" data-testid="w-cv-replace" onClick={() => cvRef.current?.click()} disabled={cv.parsing}>{cv.parsing ? <Spinner /> : t("auth.wizard.cvReplace")}</Button>
                  </div>
                ) : (
                  <Button type="button" variant="outline" data-testid="w-cv-upload" onClick={() => cvRef.current?.click()} disabled={cv.parsing} className="mt-3 w-full h-11 rounded-lg gap-2 bg-card">
                    {cv.parsing ? <><Spinner /> {t("auth.wizard.cvImporting")}</> : <><FileUp size={16} /> {t("auth.wizard.cvImportBtn")}</>}
                  </Button>
                )}
                <p className="text-[11px] text-muted-foreground mt-2">{t("auth.wizard.cvHint")}</p>
              </div>

              <FieldRow label={t("auth.wizard.title")}>
                <Input data-testid="w-title" value={pro.title} onChange={(e) => setPro({ ...pro, title: e.target.value })} placeholder={t("auth.wizard.titlePlaceholder")} className="h-11 rounded-lg" />
              </FieldRow>
              <FieldRow label={t("auth.wizard.headline")} hint={t("auth.wizard.optional")}>
                <Input data-testid="w-headline" value={pro.headline} onChange={(e) => setPro({ ...pro, headline: e.target.value })} placeholder={t("auth.wizard.headlinePlaceholder")} className="h-11 rounded-lg" />
              </FieldRow>
              <FieldRow label={t("auth.wizard.bio")} hint={t("auth.wizard.optional")}>
                <Textarea data-testid="w-bio" value={pro.bio} onChange={(e) => setPro({ ...pro, bio: e.target.value })} placeholder={t("auth.wizard.bioPlaceholder")} className="rounded-lg min-h-28" />
              </FieldRow>
              <div className="grid sm:grid-cols-3 gap-4">
                <FieldRow label={t("auth.wizard.country")}>
                  <Select value={pro.country} onValueChange={(v) => setPro({ ...pro, country: v })}>
                    <SelectTrigger data-testid="w-country" className="h-11 rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </FieldRow>
                <FieldRow label={t("auth.wizard.city")} hint={t("auth.wizard.optional")}>
                  <Input data-testid="w-city" value={pro.city} onChange={(e) => setPro({ ...pro, city: e.target.value })} placeholder="London" className="h-11 rounded-lg" />
                </FieldRow>
                <FieldRow label={t("auth.wizard.years")}>
                  <Input data-testid="w-years" type="number" min="0" value={pro.years_experience} onChange={(e) => setPro({ ...pro, years_experience: e.target.value })} className="h-11 rounded-lg tabular" />
                </FieldRow>
              </div>
              <FieldRow label={t("auth.wizard.languages")}>
                <ChipToggle options={LANGUAGES} value={pro.languages} onChange={(v) => setPro({ ...pro, languages: v })} testid="w-languages" />
              </FieldRow>
              <FieldRow label={<span className="inline-flex items-center gap-1.5"><Linkedin size={14} /> {t("auth.wizard.linkedin")}</span>} hint={t("auth.wizard.optional")}>
                <Input data-testid="w-linkedin" value={pro.linkedin} onChange={(e) => setPro({ ...pro, linkedin: e.target.value })} placeholder="https://linkedin.com/in/…" className="h-11 rounded-lg" />
              </FieldRow>
            </div>
          )}

          {/* STEP 3 — Expertise */}
          {step === 3 && (
            <div className="space-y-5 animate-fade-up" data-testid="wizard-step-3">
              <FieldRow label={t("auth.wizard.fields")} hint={t("auth.wizard.fieldsHint")}>
                <ChipToggle options={FIELDS} value={exp.fields} onChange={(v) => setExp({ ...exp, fields: v })} testid="w-fields" />
              </FieldRow>
              <FieldRow label={t("auth.wizard.expertise")}>
                <TagInput value={exp.expertise} onChange={(v) => setExp({ ...exp, expertise: v })} placeholder={t("auth.wizard.expertisePlaceholder")} testid="w-expertise" />
              </FieldRow>
              <FieldRow label={t("auth.wizard.certifications")} hint={t("auth.wizard.optional")}>
                <TagInput value={exp.certifications} onChange={(v) => setExp({ ...exp, certifications: v })} placeholder={t("auth.wizard.certificationsPlaceholder")} testid="w-certs" />
              </FieldRow>
              <FieldRow label={t("auth.wizard.achievements")} hint={t("auth.wizard.optional")}>
                <TagInput value={exp.achievements} onChange={(v) => setExp({ ...exp, achievements: v })} placeholder={t("auth.wizard.achievementsPlaceholder")} testid="w-ach" />
              </FieldRow>
            </div>
          )}

          {/* STEP 4 — Review + anti-robot */}
          {step === 4 && (
            <div className="space-y-5 animate-fade-up" data-testid="wizard-step-4">
              <div className="rounded-xl border border-border bg-secondary/30 p-4 space-y-2 text-sm">
                <Summary label={t("auth.wizard.summaryName")} value={id.name} />
                <Summary label={t("auth.wizard.summaryTitle")} value={pro.title} />
                <Summary label={t("auth.wizard.summaryFields")} value={exp.fields.join(", ")} />
                <Summary label={t("auth.wizard.summaryYears")} value={String(pro.years_experience || 0)} />
                {cv.name && <Summary label={t("auth.wizard.summaryCv")} value={cv.name} />}
              </div>

              {/* Anti-robot */}
              <div className="rounded-xl border border-border p-4" data-testid="wizard-antirobot">
                <p className="text-sm font-medium flex items-center gap-1.5 mb-3"><ShieldCheck size={15} className="text-primary" /> {t("auth.wizard.robotTitle")}</p>
                <div className="flex items-end gap-3">
                  <div className="flex-1 space-y-1.5">
                    <Label className="text-sm">{challenge ? t("auth.wizard.robotQuestion", { q: challenge.question }) : "…"}</Label>
                    <Input data-testid="w-robot-answer" inputMode="numeric" value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="?" className="h-11 rounded-lg w-28 tabular" />
                  </div>
                  <Button type="button" variant="ghost" size="sm" data-testid="w-robot-refresh" onClick={() => { loadChallenge(); setAnswer(""); }} className="rounded-lg gap-1.5 text-muted-foreground">
                    <RefreshCw size={14} /> {t("auth.wizard.robotRefresh")}
                  </Button>
                </div>
                <label className="flex items-start gap-2.5 cursor-pointer mt-3">
                  <Checkbox data-testid="w-robot-check" checked={robot} onCheckedChange={(v) => setRobot(v === true)} className="mt-0.5" />
                  <span className="text-sm text-muted-foreground">{t("auth.wizard.robotCheckbox")}</span>
                </label>
                {/* honeypot: hidden from humans */}
                <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
              </div>

              <div className="space-y-2.5">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <Checkbox data-testid="consent-privacy" checked={consent} onCheckedChange={(v) => setConsent(v === true)} className="mt-0.5" />
                  <span className="text-xs text-muted-foreground leading-relaxed">
                    <Trans i18nKey="auth.consentPrivacy" components={{ lnk: <Link to="/privacy" target="_blank" className="text-primary hover:underline" /> }} /> <span className="text-destructive">*</span>
                  </span>
                </label>
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <Checkbox data-testid="consent-terms" checked={terms} onCheckedChange={(v) => setTerms(v === true)} className="mt-0.5" />
                  <span className="text-xs text-muted-foreground leading-relaxed">
                    <Trans i18nKey="auth.consentTerms" components={{ lnk: <Link to="/terms" target="_blank" className="text-primary hover:underline" /> }} /> <span className="text-destructive">*</span>
                  </span>
                </label>
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <Checkbox data-testid="consent-marketing" checked={marketing} onCheckedChange={(v) => setMarketing(v === true)} className="mt-0.5" />
                  <span className="text-xs text-muted-foreground leading-relaxed">{t("auth.consentMarketing")}</span>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Nav */}
        <div className="mt-8 flex items-center gap-3">
          {step > 1 && (
            <Button type="button" variant="outline" data-testid="wizard-back" onClick={back} className="h-11 rounded-lg gap-1.5 bg-card">
              <ArrowLeft size={16} /> {t("auth.wizard.back")}
            </Button>
          )}
          <div className="flex-1" />
          {step < TOTAL_STEPS ? (
            <Button type="button" data-testid="wizard-next" onClick={next} className="h-11 rounded-lg gap-1.5 active:scale-[0.98] transition-transform">
              {t("auth.wizard.next")} <ArrowRight size={16} />
            </Button>
          ) : (
            <Button type="button" data-testid="wizard-submit" onClick={submit} disabled={submitting || !consent || !terms || !robot} className="h-11 rounded-lg gap-1.5 active:scale-[0.98] transition-transform">
              {submitting ? <Spinner /> : <>{t("auth.wizard.submit")} <ArrowRight size={16} /></>}
            </Button>
          )}
        </div>

        <div className="mt-6 rounded-lg border border-border bg-secondary/40 p-3.5 flex items-center justify-between gap-3">
          <span className="text-xs text-muted-foreground">{t("auth.crossCompanyQ")}</span>
          <Link to="/register/company" data-testid="switch-to-company" className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline">
            <Building2 size={13} /> {t("auth.crossCompanyLink")}
          </Link>
        </div>
        <p className="text-sm text-muted-foreground text-center mt-5">
          {t("auth.haveAccount")} <Link to="/login" data-testid="to-login" className="text-primary font-medium hover:underline">{t("common.signIn")}</Link>
        </p>
      </div>
    </WizardLayout>
  );
}

function Summary({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="font-medium text-foreground text-right break-words">{value || "—"}</span>
    </div>
  );
}

function WizardLayout({ children }) {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen grid lg:grid-cols-[0.85fr_1.15fr]">
      <div className="hidden lg:flex flex-col justify-between bg-sidebar border-r border-border p-10 relative overflow-hidden">
        <div className="absolute inset-0 dotted-bg opacity-60" />
        <div className="absolute inset-x-0 top-0 h-72 glow pointer-events-none" />
        <div className="relative"><Logo /></div>
        <div className="relative max-w-md">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary mb-4">{t("auth.badgeExpert")}</p>
          <h2 className="text-[2rem] leading-[1.18] font-semibold tracking-tight text-foreground">{t("auth.panelVision")}</h2>
          <div className="mt-8 space-y-3">
            <Trust text={t("auth.wizard.trust1")} />
            <Trust text={t("auth.wizard.trust2")} />
            <Trust text={t("auth.wizard.trust3")} />
          </div>
        </div>
        <div className="relative flex items-center gap-6 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><ShieldCheck size={14} className="text-primary" /> {t("auth.panelVerified")}</span>
          <span className="inline-flex items-center gap-1.5"><Check size={14} className="text-success" /> {t("auth.panelApproved")}</span>
        </div>
      </div>
      <div className="flex flex-col justify-center px-5 sm:px-10 py-12 bg-background relative overflow-y-auto">
        <div className="absolute top-5 right-5"><LanguageSwitcher variant="outline" /></div>
        <div className="lg:hidden mb-8 max-w-lg mx-auto w-full"><Logo /></div>
        {children}
      </div>
    </div>
  );
}

function Trust({ text }) {
  return (
    <p className="flex items-center gap-2.5 text-sm text-muted-foreground">
      <Check size={16} className="text-primary shrink-0" /> {text}
    </p>
  );
}
